import { useRobotControl } from "providers/RobotControlProvider";
import { useGimbalControl } from "providers/GimbalControlProvider";
import { useArmControl } from "providers/ArmControlProvider";
import { useFeatures } from "providers/FeaturesProvider";
import useKeyboardMovement from "hooks/useKeyboardMovement";

import JoystickWrapper from "./JoyStickWrapper";
import CenterGimbalButton from "components/control/CenterGimbalButton";
import {
    ZoomInGimbalButton,
    ZoomOutGimbalButton,
} from "components/control/ZoomGimbalButton";
import { useEffect, useRef } from "react";
import {
    ARM_ANGULAR_SPEED,
    ARM_LINEAR_SPEED,
    ARM_STICK_MODE_ROTATE,
} from "lib/consts/arm";

const JoyStickRow = ({ visible = true, className = "", speedMultiplier = 1.0 }) => {
    const { sendPerformMovement } = useRobotControl();
    const { hasGimbalControl, hasArm } = useFeatures();
    const { controlGimbal, sendGimbalSpeed, sendGimbalZoom } = useGimbalControl();
    const { controlArm, armStickMode, sendArmCmdVel, sendArmCmdVelZero } = useArmControl();

    const movementRef = useRef({ x: 0, y: 0, z: 0 });
    const lastRef = useRef({ x: 0, y: 0, z: 0 });
    // Combined tool-frame twist from both sticks (mapping depends on armStickMode)
    const armStickRef = useRef({ lx: 0, ly: 0, rx: 0, ry: 0 });
    const armStickModeRef = useRef(armStickMode);
    armStickModeRef.current = armStickMode;

    const isGimbalControlMode = hasGimbalControl && controlGimbal;
    const isArmControlMode = hasArm && controlArm;
    const isSpecialMode = isArmControlMode || isGimbalControlMode;

    const twistFromSticks = (lx, ly, rx, ry) => {
        const lin = ARM_LINEAR_SPEED * speedMultiplier;
        const ang = ARM_ANGULAR_SPEED * speedMultiplier;
        if (armStickModeRef.current === ARM_STICK_MODE_ROTATE) {
            // Left: roll + pitch; Right: yaw (horizontal). Vertical right unused.
            return {
                vx: 0,
                vy: 0,
                vz: 0,
                wx: lx * ang,
                wy: ly * ang,
                wz: -rx * ang,
            };
        }
        // Translate (default): Left X+Y (forward/back + strafe); Right Y → up/down
        return {
            vx: ly * lin,
            vy: -lx * lin,
            vz: ry * lin,
            wx: 0,
            wy: 0,
            wz: 0,
        };
    };

    const publishArmFromSticks = () => {
        const { lx, ly, rx, ry } = armStickRef.current;
        sendArmCmdVel(twistFromSticks(lx, ly, rx, ry));
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (isSpecialMode) return;
            const current = movementRef.current;
            const { x, y, z } = movementRef.current;
            if (x === 0 && y === 0 && z === 0) {
                // do nothing
            } else {
                sendPerformMovement({ x, y, z, speedMultiplier });
            }
            lastRef.current = current;
        }, 100);

        return () => {
            if (intervalId === null) return;
            clearInterval(intervalId);
        };
    }, [sendPerformMovement, speedMultiplier, isSpecialMode]);

    // Stream non-zero twists at 10 Hz while sticks are deflected. Do not heartbeat
    // zeros — idle hold / gravity compensation is server-side (watchdog + MIT hold).
    useEffect(() => {
        if (!isArmControlMode) return undefined;
        const intervalId = setInterval(() => {
            const { lx, ly, rx, ry } = armStickRef.current;
            if (lx === 0 && ly === 0 && rx === 0 && ry === 0) {
                return;
            }
            sendArmCmdVel(twistFromSticks(lx, ly, rx, ry));
        }, 100);
        return () => clearInterval(intervalId);
    }, [isArmControlMode, armStickMode, speedMultiplier, sendArmCmdVel]);

    useEffect(() => {
        const zeroAll = () => {
            movementRef.current = { x: 0, y: 0, z: 0 };
            armStickRef.current = { lx: 0, ly: 0, rx: 0, ry: 0 };
            if (hasGimbalControl && controlGimbal) {
                sendGimbalSpeed({ yaw: 0, pitch: 0 });
                sendGimbalZoom(0);
            }
            if (hasArm && controlArm) {
                sendArmCmdVelZero();
            }
        };
        const onVisibility = () => {
            if (document.hidden) zeroAll();
        };
        window.addEventListener("blur", zeroAll);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("blur", zeroAll);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [
        hasGimbalControl,
        controlGimbal,
        sendGimbalSpeed,
        sendGimbalZoom,
        hasArm,
        controlArm,
        sendArmCmdVelZero,
    ]);

    useKeyboardMovement(movementRef, { enabled: visible && !isSpecialMode });

    const onLeftMovement = (event) => {
        movementRef.current.x = event.x;
        movementRef.current.y = event.y;
    };
    const onLeftStop = () => {
        movementRef.current.x = 0;
        movementRef.current.y = 0;
    };
    const onRightMovement = (event) => {
        movementRef.current.z = event.x;
    };
    const onRightStop = () => {
        movementRef.current.z = 0;
    };

    const onGimbalMovement = (event) =>
        sendGimbalSpeed({ yaw: event.x * 30, pitch: event.y * 30 });
    const onGimbalStop = () => sendGimbalSpeed({ yaw: 0, pitch: 0 });

    const onArmLeftMove = (event) => {
        armStickRef.current.lx = event.x;
        armStickRef.current.ly = event.y;
        publishArmFromSticks();
    };
    const onArmLeftStop = () => {
        armStickRef.current.lx = 0;
        armStickRef.current.ly = 0;
        const { rx, ry } = armStickRef.current;
        if (rx === 0 && ry === 0) sendArmCmdVelZero();
        else publishArmFromSticks();
    };
    const onArmRightMove = (event) => {
        armStickRef.current.rx = event.x;
        armStickRef.current.ry = event.y;
        publishArmFromSticks();
    };
    const onArmRightStop = () => {
        armStickRef.current.rx = 0;
        armStickRef.current.ry = 0;
        const { lx, ly } = armStickRef.current;
        if (lx === 0 && ly === 0) sendArmCmdVelZero();
        else publishArmFromSticks();
    };

    return (
        visible && (
            <div
                className={`pointer-events-none grid grid-cols-2 w-[calc(100%-2.5rem)] px-8 mx-2 ${className}`}
            >
                {(!isGimbalControlMode || isArmControlMode) && (
                    <JoystickWrapper
                        className="pointer-events-auto col-start-1 justify-self-start"
                        handleMove={isArmControlMode ? onArmLeftMove : onLeftMovement}
                        handleStop={isArmControlMode ? onArmLeftStop : onLeftStop}
                    />
                )}
                <div className="pointer-events-auto col-start-2 justify-self-end flex items-center gap-4">
                    <JoystickWrapper
                        horizontalOnly={!isSpecialMode}
                        handleMove={
                            isArmControlMode
                                ? onArmRightMove
                                : isGimbalControlMode
                                  ? onGimbalMovement
                                  : onRightMovement
                        }
                        handleStop={
                            isArmControlMode
                                ? onArmRightStop
                                : isGimbalControlMode
                                  ? onGimbalStop
                                  : onRightStop
                        }
                    />
                    {isGimbalControlMode && !isArmControlMode && (
                        <div className="flex flex-col gap-2">
                            <ZoomInGimbalButton />
                            <ZoomOutGimbalButton />
                            <CenterGimbalButton />
                        </div>
                    )}
                </div>
            </div>
        )
    );
};

export default JoyStickRow;
